/**
 * Block-child MULTILEADER render arm (Codex PR #784 round 4 F1).
 *
 * A MULTILEADER is a legal block-definition child (`blockChildEntitySchema`
 * carries `multileaderEntitySchema`; the BLOCKS section has the identical
 * structure to ENTITIES per the Autodesk DXF reference / ezdxf
 * `BlockLayout`), so an INSERT of such a definition must draw the WHOLE
 * callout — every leader line, dogleg, landing, arrowhead, the MTEXT content
 * and, for `contentType === 1`, the flattened block content — placed by the
 * INSERT's affine like every other child.
 *
 * # BAKE LOCAL, THEN PLACE — the MLEADER is not a point-transformable leaf
 *   (Codex PR #784 round 6 P2 ×2)
 *
 * This arm follows the SAME model `blockFromEntity.ts`'s DIMENSION arm uses
 * (`renderBlockChildDimension` → `transformDimensionPrimitive`): generate the
 * callout in the MLEADER's OWN (block-local) frame with the production typed
 * adapter, then transform every EMITTED primitive by the enclosing INSERT
 * affine ({@link transformMultileaderPrimitive}). Rounds 4–5 instead cloned
 * the ENTITY into world space (points through the affine, scalars × |scaleX|)
 * and let the geometry builder derive world geometry from world scalars. That
 * is lossy, and round 6 found both halves of the loss:
 *
 *   - **the dogleg is a LENGTH ALONG A DIRECTION, not a magnitude.**
 *     `buildMultileaderGeometry` normalises the (transformed) `doglegVector`
 *     and multiplies by the SCALAR `doglegLength`, so under a non-uniform
 *     scale the correct factor is `|L·d̂|` — it depends on each dogleg's own
 *     direction (a vertical four-unit dogleg under `(2,3)` must become
 *     twelve, a horizontal one eight) and therefore DIFFERS PER NODE, while
 *     `MultileaderRawFields.doglegLength` is ONE entity-level scalar shared by
 *     every node. No entity-level clone can carry that. Emitting the dogleg in
 *     the local frame and transforming the resulting SEGMENT is exact for
 *     every node at once — and it fixes the `doglegVector === null` fallback
 *     too (that branch is horizontal in the frame it is computed in: local, so
 *     it must rotate with the INSERT, which a world-frame derivation lost),
 *     and the `landingGap` walk toward the content anchor with it.
 *   - **the block CONTENT is a nested REFERENCE, and its placement is a
 *     COMPOSITION.** See {@link composeBlockContentReference}.
 *
 * What the local bake does NOT change: the per-CANNOSCALE `annotativeContexts`
 * (picked in the local frame by `pickAnnotativeContext`, then transformed with
 * the geometry they produced — no per-context scalar folding any more) and the
 * MLEADERSTYLE collapse (`multileaderFromEntity`'s module doc).
 *
 * # The block CONTENT reference is COMPOSED, never collapsed to one triple
 *   (Codex PR #784 rounds 5 + 6 P2)
 *
 * `blockScale` / `blockRotation` are NOT magnitude scalars of the callout's
 * own geometry: they are the transform of a nested block REFERENCE. AutoCAD
 * stores that placement as an AFFINE — ezdxf `ezdxf/entities/mleader.py`
 * `BlockData` carries the content location (CONTEXT_DATA 15/25/35), a block
 * scale VECTOR (16/26/36), the rotation (46) and, redundantly, the composed
 * 4×4 matrix (47 × 16 doubles); the AcDbMLeader BODY carries the same as
 * scalar 45 (scale) / 43 (rotation) plus the `block_scale_vector` at 10/20/30
 * (this repo's parser: `packages/dxf-io/src/entityParser.ts` +
 * `mleaderContextBuilder.ts`). An INSERT of the enclosing definition then
 * places the WHOLE definition — the MLEADER's block content included —
 * through its own affine `T(insertion)·R(rotation)·S(scaleX,scaleY)·T(−base)`
 * (Autodesk DXF INSERT groups 10/20/30, 41/42/43, 50; `scene/blockMatrix.ts`'s
 * `composeBlockMatrix` is this repo's one-truth for that order), NEGATIVE
 * (mirroring) and non-uniform components included.
 *
 * The world placement of the content is therefore `M_insert ∘ M_content`, and
 * {@link composeBlockContentReference} builds exactly that — including the
 * inner rotation, so nothing is approximated away.
 *
 * # Import-cycle note
 *
 * This module imports `multileaderFromEntity`; `blockFromEntity.ts` imports
 * this module. The reverse edge (multileader → block flatten) is INJECTED as
 * a {@link BlockContentFlattener}, never imported — see
 * `./blockReferenceFlatten.ts` for the full rationale and the dependency
 * -cruiser zero-cycle constraint it satisfies.
 */

import type {
  BlockDefinition,
  MultileaderEntity,
} from "@dxf-ai-editor/drawing-model";

import { composeBlockLinearExact } from "@dxf-ai-editor/cad-core";

import type {
  MTextRun,
  MultileaderPrimitive,
  RenderableEntity,
  Vec3,
} from "../../types";

import type { AdapterContext } from "../adapterContext";
import { bakeChildByLayerLinetype } from "../geom/multileaderGeometry";

import type {
  BlockContentFlattener,
  BlockReferenceFlattenArgs,
} from "./blockReferenceFlatten";
import {
  bakeBlockChildLocal,
  blockLinearOfTransform,
  blockScalarRotationDeg,
  emittedPointMapper,
  emittedUnitDirectionMapper,
  placeGlyphAffine,
  transformBlockChildPoint,
  transformEmittedBounds,
  type BlockInsertTransform,
} from "./blockTransform";
import { multileaderFromEntity } from "./multileaderFromEntity";

/**
 * Render ONE block-child MULTILEADER: bake the callout in the MLEADER's own
 * (block-local) frame through the production typed adapter, then place every
 * emitted primitive with the enclosing INSERT affine (module doc "BAKE LOCAL,
 * THEN PLACE").
 *
 * `blocks` is threaded onto the adapter context so the callout's block content
 * resolves against the SAME block table the enclosing flatten walks (the
 * legacy render branch passes its `inputs.blockDefinitions` explicitly and may
 * leave `ctx.blocks` unset — without this the block content would silently
 * vanish on that branch only).
 *
 * # Why the block content is flattened OUTSIDE the emitter
 *
 * The emitter (`../geom/multileaderGeometry.ts`) asks its injected flattener
 * for the content reference in the LOCAL frame and then EXTENDS the callout's
 * bbox with each returned child's `bounds`. Those bounds are the flatten's
 * own output space — WORLD — so letting the emitter see world children while
 * every other primitive is local would union two different frames into one
 * bbox and blow the renderable's bounds far past its geometry. So the injected
 * flattener CAPTURES the local reference and returns nothing; the ONE real
 * flatten then runs here, on the composed world reference, and its children
 * are handed to {@link transformMultileaderPrimitive} for the `block-content`
 * primitive plus unioned into the world bounds. `bakeChildByLayerLinetype` —
 * the emitter's own ByLayer-linetype bake, which the re-home in
 * `scene/renderSatellites.ts` would otherwise corrupt — is applied here from
 * the SAME shared helper, so there is still exactly one implementation.
 *
 * The capture keeps `args.flattenBlockContent`'s recursion guard intact: it is
 * the enclosing flatten's own closure, invoked once, with only the reference
 * transform rewritten.
 */
export function renderBlockChildMultileader(args: {
  child: MultileaderEntity;
  basePoint: Vec3;
  t: BlockInsertTransform;
  ctx: AdapterContext;
  blocks: Record<string, BlockDefinition>;
  identity: { id: string; spaceId: string; parentLayer: string };
  flattenBlockContent: BlockContentFlattener;
}): RenderableEntity {
  const { child, basePoint, t, ctx, blocks, identity } = args;
  // Per-instance identity + AutoCAD layer-"0" inheritance + the enclosing block
  // table on the context — the ONE shared re-homing every composite child arm
  // does before running its adapter in the block-LOCAL frame (see
  // {@link bakeBlockChildLocal}). Every coordinate and every scalar stays
  // AUTHORED — that is what makes the placement below the single, exact
  // transform.
  const { local, adapterCtx } = bakeBlockChildLocal<MultileaderEntity>(
    child,
    identity,
    ctx,
    blocks,
  );

  let contentRequest: BlockReferenceFlattenArgs | null = null;
  const captureContent: BlockContentFlattener = (contentArgs) => {
    contentRequest = contentArgs;
    return [];
  };
  const localRenderable = multileaderFromEntity(
    local,
    adapterCtx,
    captureContent,
  );
  const request: BlockReferenceFlattenArgs | null = contentRequest;
  const worldContent: RenderableEntity[] =
    request === null
      ? []
      : args
          .flattenBlockContent(
            composeBlockContentReference(request, basePoint, t),
          )
          .map((f) => bakeChildByLayerLinetype(f, adapterCtx));

  if (localRenderable.geometry.kind !== "multileader") return localRenderable;
  const primitives = localRenderable.geometry.primitives.map((p) =>
    transformMultileaderPrimitive(p, worldContent, basePoint, t),
  );
  return {
    ...localRenderable,
    bounds: unionContentBounds(
      transformEmittedBounds(localRenderable.bounds, basePoint, t),
      worldContent,
    ),
    geometry: { ...localRenderable.geometry, primitives },
  };
}

/**
 * Compose the ENCLOSING INSERT affine onto the MLEADER's OWN block-content
 * reference: the emitter asks for the content reference in the MLEADER's own
 * frame (`insertion` = the RAW local content anchor, `scale` = the authored
 * `blockScale` on both axes, `rotationDeg` = `blockRotation`), and this
 * returns the flatten arguments for `M_insert ∘ M_content` — EXACTLY, with no
 * shear residual.
 *
 * It composes the arguments it RECEIVES — never a copy of the entity's fields
 * — so anything the emitter folds into the content reference (the block
 * colour / linetype thread, the MLEADER layer, the ATTRIB substitution rows)
 * rides the composition instead of being silently replaced.
 *
 * # The algebra
 *
 * With `L(x) = R(θ)·S(a,b)·R(φ)` (the {@link BlockInsertTransform} linear part,
 * `preRotationDeg` included) and the content reference's own
 * `M_c = T(ins_c)·R(θ_c)·S(c,c)·T(−b_c)`:
 *
 *   - **insertion** → `M_insert(ins_c)` (`transformBlockChildPoint`, which also
 *     applies the Z half of the affine, so the content sits in the callout's
 *     plane instead of at Z 0). `M_i ∘ T(ins_c) ≡ T(M_i(ins_c)) ∘ L_i`, which
 *     is what lets the rest of the composition be purely linear.
 *   - **scale** → the SIGNED, per-axis product `t.scale.* × content.scale.*`,
 *     so a mirror stays a mirror and a non-uniform enclosing scale keeps BOTH
 *     magnitudes (one positive scalar cannot — round 5's finding).
 *   - **rotation / preRotation** → the enclosing `rotationDeg` stays OUTER and
 *     the content's own rotation joins the INNER one:
 *     `R(θ)S(a,b)R(φ) · R(θ_c)S(c,c) = R(θ)·S(a·c, b·c)·R(φ+θ_c)`, because a
 *     UNIFORM scale commutes with a rotation. Round 5 folded `θ_c` into the
 *     outer rotation instead (with a reflection sign flip), which is
 *     `R(θ±θ_c)·S(a·c,b·c)` — equal ONLY when `|a| = |b|`; otherwise
 *     `S(a,b)·R(θ_c)` is a genuine SHEAR that the fold silently dropped
 *     (round 6's finding: an INSERT scaled `(3,2)` around a 45°-rotated
 *     content reference drew the content outside the INSERT's own metadata
 *     bounds). Carrying `θ_c` as the inner rotation of the SVD-shaped triple
 *     is that same product, exactly.
 *
 * # Reachable limits
 *
 * The commutation step needs the CONTENT scale to be uniform in X/Y, and it
 * always is: the sole production caller builds it from the entity's single
 * scalar `blockScale` (`../geom/multileaderGeometry.ts`:
 * `scale: vec3(raw.blockScale, raw.blockScale, 1)`), which is DXF group 45 /
 * CONTEXT_DATA 46 — AutoCAD's own per-content scale is one number. Round 7
 * (Codex P2) nevertheless routes this through `cad-core`'s
 * `composeBlockLinearExact` rather than spelling the commuted product out
 * here: that helper TAKES the uniform fast path (byte-identical numbers) and
 * falls back to a full 2×2 SVD of `S(a,b)·R(φ)·S(cx,cy)` for a non-uniform
 * content scale — which this argument shape can express even though no
 * production caller produces one — instead of silently folding it to the wrong
 * side of the inner rotation.
 *
 * Everything a content CHILD then rides is the shared flatten's own fidelity
 * (`transformBlockChild`) — the non-uniform Circle → ellipse promotion and the
 * glyph shear stay the repo-wide S10-XFORM delta, identical to what the same
 * child would get from a plain nested INSERT.
 */
export function composeBlockContentReference(
  content: BlockReferenceFlattenArgs,
  basePoint: Vec3,
  t: BlockInsertTransform,
): BlockReferenceFlattenArgs {
  // ONE truth with the nested-INSERT arm and with the block-BOUNDS walk:
  // `cad-core`'s `composeBlockLinearExact` (Codex PR #784 round 7 P2). For the
  // UNIFORM content scale the sole production caller builds (see "Reachable
  // limits" above) it returns exactly the pre-existing expression — the
  // per-axis product with the content rotation carried INTO the inner slot —
  // byte-identical. A non-uniform content scale, unreachable today but
  // expressible in this argument shape, is decomposed instead of being folded
  // to the wrong side of the inner rotation.
  const composed = composeBlockLinearExact(blockLinearOfTransform(t), {
    rotationDeg: content.rotationDeg,
    scaleX: content.scale.x,
    scaleY: content.scale.y,
  });
  return {
    ...content,
    insertion: transformBlockChildPoint(content.insertion, basePoint, t),
    scale: {
      x: composed.scaleX,
      y: composed.scaleY,
      z: t.scale.z * content.scale.z,
    },
    rotationDeg: composed.rotationDeg,
    preRotationDeg: composed.preRotationDeg,
  };
}

/**
 * Place ONE locally-baked `MultileaderPrimitive` with the block INSERT affine
 * — the MULTILEADER twin of `blockTransform.ts`'s `transformDimensionPrimitive`,
 * sharing its emitted-point / emitted-direction mappers so the two arms cannot
 * drift.
 *
 * Conventions are the ones `blockTransform.ts`'s module header pins for every
 * other child, applied to the EMITTED geometry instead of to entity fields:
 *
 *   - every POSITION (leader vertices, dogleg/landing endpoints, arrow tip,
 *     content anchors) → the full affine. Lengths that were derived along a
 *     direction in the local frame (`doglegLength`, `landingGap`) are already
 *     baked into those endpoints, so they scale by the true `|L·d̂|` per
 *     segment — the round-6 (b) fix;
 *   - the arrow GLYPH → the FULL enclosing linear map, composed onto the
 *     locally-emitted `Rz(direction)·S(size,size)` pose and shipped as the
 *     primitive's `glyphLinear` (`blockTransform.ts::placeGlyphAffine`, the ONE
 *     truth shared with the block-child DIMENSION arm) — Codex PR #784 round 11
 *     P2. Its companion scalars stay populated and stay HONEST: the DIRECTION is
 *     the linear part re-normalised (the consumer stores a unit vector) and the
 *     SIZE is the arrowhead's true ALONG-ITS-OWN-AXIS length — `size · |L·û|`
 *     for the glyph's own axis `û` (`arrowMarkerMesh.ts::glyphSizeAxis`: the
 *     body for an arrowhead, the slash for an `_OBLIQUE` / `_ARCHTICK` tick,
 *     which `dimblkToVariant` can hand this arm) — the same "a length along a
 *     direction is not a magnitude" rule the dogleg takes above, and identical
 *     to the previous `size · |scaleX|` for every uniformly-scaled INSERT. Both
 *     stay non-negative, so a mirror still never negates a length;
 *   - the MTEXT glyph HEIGHT (primitive + each run's `heightWorld` /
 *     `baselineOffsetWorld`, which the emitter derived linearly from it) →
 *     × |scaleY|, and each run's `widthFactor` → × |scaleX|/|scaleY| so the
 *     horizontal metric equals `height · widthFactor · |scaleX|` (the TEXT
 *     glyph-aspect convention, @codex #431 P2; exactly 1 under uniform scale);
 *   - the absolute MTEXT ANGLE (DEGREES at the render-feed layer) → +
 *     {@link blockScalarRotationDeg} (double-negative 180° fold and inner
 *     rotation included);
 *   - `mirrored` is deliberately NOT flipped under a mirroring transform — the
 *     same decision `blockTransform.ts` records for TEXT/MTEXT glyph
 *     handedness ("MIRRORED TEXT decision": glyph reflection is the S10-XFORM
 *     delta, not this flatten's).
 *
 * `block-content.flattened` is REPLACED with `worldContent`, never mapped:
 * those renderables are arbitrary already-emitted per-kind geometry (hatch
 * loops, text meshes, splines) and re-transforming them would be a second,
 * weaker copy of the flatten's own transform. They are produced ONCE, directly
 * in world space, by the composed reference
 * ({@link composeBlockContentReference}). The primitive's scalar `scale` /
 * `rotation` keep reporting the MLEADER's OWN content transform — identical to
 * what a TOP-LEVEL MLEADER reports, and the composed world reference is not a
 * scalar pair anyway. Their ONE consumer is the PDF exporter's
 * `print/pdf/converters/multileaderConverter.ts`, reached only for a top-level
 * MLEADER (`print/pdf/converters/insertConverter.ts`'s child switch has no
 * `Multileader` arm at all — block-child MLEADERs are dropped from the plot, a
 * pre-existing print gap tracked outside this render-side fix).
 */
export function transformMultileaderPrimitive(
  prim: MultileaderPrimitive,
  worldContent: RenderableEntity[],
  basePoint: Vec3,
  t: BlockInsertTransform,
): MultileaderPrimitive {
  const xfPoint = emittedPointMapper(basePoint, t);
  const xfDir = emittedUnitDirectionMapper(t);
  const heightScale = Math.abs(t.scale.y);
  switch (prim.kind) {
    case "leader-line":
      return { ...prim, vertices: prim.vertices.map(xfPoint) };
    case "dogleg":
    case "landing":
      return { ...prim, a: xfPoint(prim.a), b: xfPoint(prim.b) };
    case "arrow":
      return {
        ...prim,
        tip: xfPoint(prim.tip),
        direction: xfDir(prim.direction),
        // ONE truth with the block-child DIMENSION arm's arrow/tick marker
        // (`blockTransform.ts::placeGlyphAffine`): the MLEADER emitter carries
        // an `ArrowVariant` too (`multileaderGeometry.ts` builds it with
        // `dimblkToVariant`), so an `_OBLIQUE` / `_ARCHTICK` arrowhead reaches
        // here as a TICK variant and its companion `size` must be measured
        // along the glyph's OWN axis (its slash), not blindly off the body
        // column — the same fix, the same helper.
        ...placeGlyphAffine(prim.direction, prim.size, prim.variant, t),
      };
    case "mtext-content":
      return {
        ...prim,
        anchor: xfPoint(prim.anchor),
        rotation: prim.rotation + blockScalarRotationDeg(t),
        height: prim.height * heightScale,
        runs: prim.runs.map((run) => scaleMTextRun(run, t)),
      };
    case "block-content":
      return { ...prim, anchor: xfPoint(prim.anchor), flattened: worldContent };
  }
}

/** Glyph metrics of ONE emitted MTEXT run under the block scale — the run-level
 *  twin of `transformBlockChild`'s TEXT `height · |scaleY|` +
 *  `widthFactor · |scaleX|/|scaleY|` pair (see
 *  {@link transformMultileaderPrimitive}). Everything else on the run is a
 *  dimensionless style flag and rides the spread untouched. */
function scaleMTextRun(run: MTextRun, t: BlockInsertTransform): MTextRun {
  const heightScale = Math.abs(t.scale.y);
  return {
    ...run,
    heightWorld: run.heightWorld * heightScale,
    baselineOffsetWorld: run.baselineOffsetWorld * heightScale,
    widthFactor: (run.widthFactor * Math.abs(t.scale.x)) / heightScale,
  };
}

/**
 * Union the WORLD bounds of the block content into the placed callout's
 * bounds. The content children are flattened outside the emitter (see
 * {@link renderBlockChildMultileader}), so the transformed local bbox — which
 * saw none of them — has to be widened here or a callout whose content
 * overhangs its leader would cull/pick short.
 */
function unionContentBounds(
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  worldContent: readonly RenderableEntity[],
): { minX: number; minY: number; maxX: number; maxY: number } {
  if (worldContent.length === 0) return bounds;
  let { minX, minY, maxX, maxY } = bounds;
  for (const f of worldContent) {
    minX = Math.min(minX, f.bounds.minX);
    minY = Math.min(minY, f.bounds.minY);
    maxX = Math.max(maxX, f.bounds.maxX);
    maxY = Math.max(maxY, f.bounds.maxY);
  }
  return { minX, minY, maxX, maxY };
}
